import { CurrentTags, PrSummary } from './api/types';

export type StageKey = 'build' | 'stg' | 'prd' | 'all';

/**
 * Whether a PR belongs to a given stage. Each deploy PR maps to exactly one
 * stage by its `env/<env>` label.
 *
 * With the `deploy/all` project.json tag a single deploy PR targets every
 * environment at once and is labelled `env/all`; it gets its own `all` stage
 * ("Deploy → stg/prd") rather than appearing under both stg and prd.
 */
export function matchesStage(pr: PrSummary, stage: StageKey): boolean {
  if (stage === 'build') return pr.prType === 'tag';
  if (pr.prType !== 'deploy') return false;
  return pr.env === stage; // 'stg' | 'prd' | 'all'
}

export interface StageBadge {
  kind: 'Build' | 'Deploy';
  /** Environment shown after the kind (deploy PRs only). */
  env?: string;
}

/** The stage a PR belongs to, for the flat Open Pull Requests list chip. */
export function stageOf(pr: PrSummary): StageBadge {
  if (pr.prType === 'tag') return { kind: 'Build' };
  return {
    kind: 'Deploy',
    env: pr.env === 'all' ? 'stg/prd' : pr.env ?? undefined,
  };
}

/** Sort order for the flat list: build → stg → prd → all/other. */
export function stageRank(pr: PrSummary): number {
  if (pr.prType === 'tag') return 0;
  if (pr.env === 'stg') return 1;
  if (pr.env === 'prd') return 2;
  return 3;
}

/**
 * The env tag a deploy PR would replace, for a row's "current → pending" hint.
 * Build PRs and combined (env/all) PRs have no single current tag.
 */
export function currentTagFor(pr: PrSummary, tags: CurrentTags): string | null {
  if (pr.prType !== 'deploy') return null;
  if (pr.env === 'stg') return tags.stg;
  if (pr.env === 'prd') return tags.prd;
  return null;
}
